import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Hidden,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { Close, GetApp, InsertDriveFile } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import React, { useState } from "react";
import { getMatchSummaryForRegistration } from "../../Base/Services/MatchService";
import { displayName, displayAddress } from "../../Admin/helper/DisplayUtil";
import { downloadRegistrationDocument } from "../../Base/Services/RegistrationDocumentService";
import { RegistrationType } from "../../Base/AdoptionConstants";

const useStyles = makeStyles((theme) => ({
  contactInfo: {
    paddingLeft: 50,
  },
  documents: {
    paddingTop: 20,
    paddingLeft: 0,
  },
  responsiveDialog: {
    [theme.breakpoints.down("xs")]: {
      margin: 0,
      maxWidth: "100%",
      width: "100%",
      height: "100%",
      maxHeight: "none",
      borderRadius: 0,
    },
  },
  compactTitle: {
    [theme.breakpoints.down("sm")]: {
      padding: "0.25rem 0.5rem",
    },
  },
  compactActions: {
    [theme.breakpoints.down("sm")]: {
      padding: "0.25rem 0.5rem",
    },
  },
  compactContent: {
    [theme.breakpoints.down("sm")]: {
      padding: "0.25rem 0.5rem",
    },
  },
}));

function getMatchMessage(registrationType) {
  const baseMessage = "Based on the information you supplied, you have been matched to ";
  if (registrationType === RegistrationType.ADOPTEE.value) {
    return baseMessage + "an adoptee.";
  } else if (registrationType === RegistrationType.BIRTH_PARENT.value) {
    return baseMessage + "a birth parent.";
  } else if (registrationType === RegistrationType.SIBLING.value) {
    return baseMessage + "a sibling.";
  }
}

export default function MatchInformationDialog({ open, onClose, registrationId, ariaLabel, ...props }) {
  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState(null);
  const classes = useStyles();

  React.useEffect(() => {
    if (open) {
      setIsLoading(true);
      getMatchSummaryForRegistration(registrationId)
        .then(setDocuments)
        .finally(() => setIsLoading(false));
    } else {
      setDocuments(null);
    }
  }, [registrationId, open]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
      }}
      fullWidth={true}
      maxWidth="sm"
      aria-label={ariaLabel}
      classes={{ paper: classes.responsiveDialog }}
      {...props}
    >
      <DialogTitle>
        <span style={{ display: "flex", justifyContent: "space-between" }}>
          <span>Match Information</span>
          <IconButton size="small" color="default" onClick={onClose}>
            <Close />
          </IconButton>
        </span>
      </DialogTitle>
      <DialogContent>
        {documents && !isLoading && (
          <div>
            {documents?.infoFromMatches.map((row, index) => (
              <div key={index}>
                <Typography variant="body1" paragraph>
                  {getMatchMessage(row.typeOfMatch)}
                </Typography>
                {documents.wantToReceiveContactInfo === false && (
                  <Typography variant="body2" paragraph>
                    You have elected not to receive any contact information from your match (you can update this choice
                    at any time)
                  </Typography>
                )}
                {documents.wantToReceiveContactInfo === true && !row.matchSharedContactInfo && (
                  <Typography variant="body2" paragraph>
                    Your match has elected not to share contact information at this time.
                  </Typography>
                )}
                {row.sharedContactInfoFromMatch && (
                  <div>
                    <Typography variant="body1" paragraph>
                      Contact Information for your match:
                    </Typography>
                    {row.isIntermediary && (
                      <Typography variant="body2" paragraph>
                        The contact information below is for an intermediary or "go-between" that your birth parent has
                        selected to receive and give information about them. You can contact this person to learn more
                        about your birth parent and to possibly exchange additional information.
                      </Typography>
                    )}
                    <div className={classes.contactInfo}>
                      <Typography variant="body1">
                        Name:{" "}
                        <strong>
                          {displayName(
                            row.sharedContactInfoFromMatch.firstName,
                            row.sharedContactInfoFromMatch.middleName,
                            row.sharedContactInfoFromMatch.lastName
                          )}
                        </strong>
                      </Typography>

                      {row.sharedContactInfoFromMatch.phone && (
                        <Typography variant="body1">
                          Phone: <strong>{row.sharedContactInfoFromMatch.phone}</strong>
                        </Typography>
                      )}

                      {displayAddress(
                        row.sharedContactInfoFromMatch.streetAddress1,
                        row.sharedContactInfoFromMatch.streetAddress2,
                        row.sharedContactInfoFromMatch.city,
                        row.sharedContactInfoFromMatch.state,
                        row.sharedContactInfoFromMatch.zipCode,
                        "",
                        row.sharedContactInfoFromMatch.country
                      ) && (
                        <Typography variant="body1">
                          Address:{" "}
                          <strong>
                            {displayAddress(
                              row.sharedContactInfoFromMatch.streetAddress1,
                              row.sharedContactInfoFromMatch.streetAddress2,
                              row.sharedContactInfoFromMatch.city,
                              row.sharedContactInfoFromMatch.state,
                              row.sharedContactInfoFromMatch.zipCode,
                              "",
                              row.sharedContactInfoFromMatch.country
                            )}
                          </strong>
                        </Typography>
                      )}

                      {row.sharedContactInfoFromMatch.email && (
                        <Typography variant="body1">
                          Email: <strong>{row.sharedContactInfoFromMatch.email}</strong>
                        </Typography>
                      )}
                    </div>
                  </div>
                )}
                {Boolean(row.sharedDocuments?.length) && (
                  <>
                    <Typography vairaint="body1" paragraph className={classes.documents}>
                      Documents your match has shared with you:
                    </Typography>
                    <List>
                      {row?.sharedDocuments?.map((r, i) => (
                        <ListItem hidden key={i} button onClick={() => downloadRegistrationDocument(r)}>
                          <Hidden xsDown>
                            <ListItemIcon>
                              <InsertDriveFile />
                            </ListItemIcon>
                          </Hidden>
                          <ListItemText
                            primary={
                              <span
                                style={{
                                  textOverflow: "ellipsis",
                                  maxWidth: "calc(100% - 80px)",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  display: "block",
                                }}
                              >
                                {r.documentName}
                              </span>
                            }
                          ></ListItemText>
                          <ListItemSecondaryAction>
                            <IconButton onClick={() => downloadRegistrationDocument(r)}>
                              <GetApp />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </div>
            ))}
            {Boolean(documents.stateDocuments?.length) && (
              <>
                <Typography vairaint="body1" paragraph className={classes.documents}>
                  Documents from Office of Vital Records and Statistics:
                </Typography>
                <List>
                  {documents?.stateDocuments?.map((r, i) => (
                    <ListItem hidden key={i} button onClick={() => downloadRegistrationDocument(r)}>
                      <Hidden xsDown>
                        <ListItemIcon>
                          <InsertDriveFile />
                        </ListItemIcon>
                      </Hidden>
                      <ListItemText
                        primary={
                          <span
                            style={{
                              textOverflow: "ellipsis",
                              maxWidth: "calc(100% - 80px)",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              display: "block",
                            }}
                          >
                            {r.documentName}
                          </span>
                        }
                      ></ListItemText>
                      <ListItemSecondaryAction>
                        <IconButton onClick={() => downloadRegistrationDocument(r)}>
                          <GetApp />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </div>
        )}
        {isLoading && (
          <>
            <Skeleton variant="text" />
            <Skeleton variant="rect" style={{ marginTop: 12, height: 60 }} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
